import { useCallback, useEffect, useState } from "react";
import { PostType } from "../../Components/Post/Post.types";
import { groupPostsFetcher } from "../../DataFetchers/GroupPostsFetcher";
import { SORT_CRITERIA_LIST } from "@shared/consts";
import { useGroupSelector } from "../../Components/GroupSelector/useGroupSelector";
import { useInterval } from "react-use";
import { useOnPostReaction } from "../../Components/Reactions/Hooks/useOnPostReaction";
import { updateUserReactionOnObject } from "@infrastructure/Hooks/UserInteractions/useUserReactions";
import { ReactionableObjectEnum } from "@infrastructure/Hooks/UserInteractions/UserInteractions.types";
import {
    ReactionActionState,
    SWITCHING_STATE_TO_IDLE_TIME,
    useReactionActionStore,
} from "../../Components/ReactionsTooltip/ReactionActionState";
import {
    addSubscriber,
    removeSubscriber,
} from "@infrastructure/Events/EventBus";
import { POST_DETAILS_CHANGED } from "../../Components/Post/PostForm/events";
import { REFRESH_LIST_EVENT } from "../../Components/DashboardComponents/PostList/PostsList";

const DELAY_INTERVAL_MILLISECONDS = 60 * 1000;

const pageRelatedProps = {
    boxed: true,
};

const fetchPromoPosts = async (groupId: string): Promise<PostType[]> => {
    const apiPosts = await groupPostsFetcher(
        {
            groupId,
            sortCriteria: SORT_CRITERIA_LIST[1].criterion,
        },
        pageRelatedProps,
    );

    return apiPosts.map((post: PostType) => ({
        ...post,
        status: {
            isHighlighted: false,
            isEditorial: post.postData.isEditorial,
        },
    }));
};

export const usePromoSliderPosts = () => {
    const [posts, setPosts] = useState<PostType[]>([]);
    const [postsLoaded, setPostsLoaded] = useState(false);
    const { mainGroupId } = useGroupSelector();

    const fetchPosts = useCallback(async () => {
        if (!mainGroupId) {
            return;
        }
        const fetchedPosts = await fetchPromoPosts(mainGroupId);
        setPostsLoaded(true);
        setPosts(fetchedPosts);
    }, [mainGroupId]);

    const { setReactionActionState } = useReactionActionStore();
    useOnPostReaction(setPosts, (event) => {
        updateUserReactionOnObject(
            event.entityId.toString(),
            event.reactions,
            ReactionableObjectEnum.POST,
        );
        setTimeout(
            () => setReactionActionState(ReactionActionState.IDLE),
            SWITCHING_STATE_TO_IDLE_TIME,
        );
    });

    useEffect(() => {
        const postDetailsChangedSubscriber = addSubscriber(
            POST_DETAILS_CHANGED,
            (eventData) => {
                const data = eventData.body.data as PostType;
                const foundPostIndex = posts.findIndex(
                    ({ id }) => id === data.id,
                );
                if (foundPostIndex >= 0) {
                    const copyPosts = [...posts];
                    copyPosts.splice(foundPostIndex, 1, {
                        ...data,
                        pageRelated: pageRelatedProps,
                    });
                    setPosts(copyPosts);
                }
            },
        );

        return () => {
            removeSubscriber(postDetailsChangedSubscriber);
        };
    }, [posts]);

    useEffect(() => {
        const refreshListSubscriber = addSubscriber(REFRESH_LIST_EVENT, () =>
            fetchPosts(),
        );
        return () => {
            removeSubscriber(refreshListSubscriber);
        };
    }, [fetchPosts]);

    useEffect(() => {
        fetchPosts();
    }, [fetchPosts]);

    useInterval(
        () => fetchPosts(),
        postsLoaded ? DELAY_INTERVAL_MILLISECONDS : null,
    );

    return { posts, setPosts };
};
